import {prepareNativeNotificationRead} from "./nativeBadge";
import {DWORKS_API_BASE} from "../constants/dworksConfig";
import {C} from "../constants/appConstants";

function formatNotificationTime(value){
  if(!value) return "";

  try{
    const date=new Date(value);
    if(Number.isNaN(date.getTime())) return String(value).slice(0,16);

    return date.toLocaleString("ko-KR",{
      month:"numeric",
      day:"numeric",
      hour:"2-digit",
      minute:"2-digit"
    });
  }catch(error){
    return String(value).slice(0,16);
  }
}

function normalizeNotificationData(value){
  if(!value) return {};
  if(typeof value==="object") return value;

  try{
    const parsed=JSON.parse(value);
    return parsed && typeof parsed==="object" && !Array.isArray(parsed) ? parsed : {};
  }catch(error){
    return {};
  }
}

function compactText(value){
  return String(value||"").replace(/\s+/g," ").trim();
}

const LOCAL_ORDER_NOTIFICATION_STORAGE_KEY="dworks:localOrderNotifications";

function readLocalOrderNotifications(){
  if(typeof window==="undefined"||!window.localStorage) return [];

  try{
    const raw=window.localStorage.getItem(LOCAL_ORDER_NOTIFICATION_STORAGE_KEY);
    const parsed=raw?JSON.parse(raw):[];
    return Array.isArray(parsed)?parsed:[];
  }catch(error){
    return [];
  }
}

function writeLocalOrderNotifications(rows){
  if(typeof window==="undefined"||!window.localStorage) return;

  try{
    window.localStorage.setItem(LOCAL_ORDER_NOTIFICATION_STORAGE_KEY,JSON.stringify((rows||[]).slice(0,40)));
  }catch(error){
    // localStorage 저장이 막힌 환경에서는 서버 알림만 유지합니다.
  }
}

function getNotificationOrderId(item){
  const data=normalizeNotificationData(item?.data);
  return String(data?.order_id||item?.order_id||"").trim();
}

function getNotificationDedupKey(item){
  const orderId=getNotificationOrderId(item);
  const data=normalizeNotificationData(item?.data);
  const source=String(data?.source||"").trim();
  const type=String(item?.type||"").trim();

  if(orderId&&source==="order_page_completion") return `order_completion:${orderId}:${type}`;
  if(item?.id) return `id:${item.id}`;
  return `${type}:${String(item?.title||"").trim()}:${String(item?.created_at||"").trim()}`;
}

function mergeNotificationsWithLocal(serverRows,userId){
  const merged=[];
  const seen=new Set();
  const pushRow=(row)=>{
    if(!row) return;
    const key=getNotificationDedupKey(row);
    if(seen.has(key)) return;
    seen.add(key);
    merged.push(row);
  };

  (Array.isArray(serverRows)?serverRows:[]).forEach(pushRow);
  readLocalOrderNotifications().filter(row=>userId && String(row?.user_id)===String(userId) && !isChatNotification(row)).forEach(pushRow);

  return merged.sort((a,b)=>{
    const at=new Date(a?.created_at||0).getTime()||0;
    const bt=new Date(b?.created_at||0).getTime()||0;
    return bt-at;
  }).slice(0,80);
}

function markLocalNotificationRead(notificationId){
  const rows=readLocalOrderNotifications();
  const readAt=new Date().toISOString();
  const next=rows.map(row=>String(row?.id)===String(notificationId)?{...row,is_read:true,read_at:readAt}:row);
  writeLocalOrderNotifications(next);
  return next.find(row=>String(row?.id)===String(notificationId));
}

function markAllLocalNotificationsRead(userId){
  const rows=readLocalOrderNotifications();
  const readAt=new Date().toISOString();
  const next=rows.map(row=>row?.is_read || String(row?.user_id)!==String(userId)?row:{...row,is_read:true,read_at:readAt});
  writeLocalOrderNotifications(next);
}

function getNotificationRawText(item){
  const data=normalizeNotificationData(item?.data);
  return compactText([
    item?.type,
    item?.title,
    item?.body,
    data?.response_label,
    data?.response_status,
    data?.shipment_status,
    data?.cancel_reason,
    data?.reason,
    data?.target_name,
    data?.vendor_name,
    data?.item_name,
    data?.option_name
  ].filter(Boolean).join(" "));
}

async function requestNotificationApi(path, options = {}){
  const { token, method = "GET", payload = null } = options;
  const headers = { "Content-Type": "application/json" };

  if(token){
    headers.Authorization = `Bearer ${token}`;
  }

  const finishNativeRead = prepareNativeNotificationRead(path, method);
  const res = await fetch(`${DWORKS_API_BASE}${path}`, {
    method,
    headers,
    body: payload === null || payload === undefined ? undefined : JSON.stringify(payload)
  });

  const data = await res.json().catch(()=>null);

  if(!res.ok || data?.ok === false){
    throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  }

  finishNativeRead(data);
  return data;
}

// Classify structured event fields, never words from a message or a product name.
export function getNotificationGroup(item){
  if(isChatNotification(item)) return "chat";
  const data=normalizeNotificationData(item?.data);
  const type=String(item?.type||data.type||"");
  if(["factory_shipment_confirmed","vendor_shipment","vendor_shipment_cancelled"].includes(type)) return "shipment";
  if(type==="factory_production_status") return "production";
  if(type==="vendor_feedback"){
    if(data.shipment_status || ["shipping_today","shipped","partially_shipped","partial_shipped","shipment_completed"].includes(data.response_status)) return "shipment";
    return data.target_type==="factory" ? "production" : "vendor";
  }
  if(type==="vendor_unit_price_proposed") return "vendor";
  if(["order","order_completed","order_issue","order_status_changed","dispatch_failed","scheduled_order_completed","scheduled_order_failed"].includes(type)) return "order";
  return "other";
}

export const NOTIFICATION_FILTERS=[
  {key:"all",label:"전체"},{key:"order",label:"발주"},{key:"production",label:"생산"},
  {key:"shipment",label:"출고"},{key:"vendor",label:"공급처"}
];

function getNotificationCategory(item){
  const group=getNotificationGroup(item);
  const type=String(item?.type||"");
  const data=normalizeNotificationData(item?.data);
  if(group==="chat") return {label:"대화",color:C.acc,icon:"chat"};
  if(group==="production") return {label:"생산 상태",color:C.acc,icon:"shirt"};
  if(group==="shipment") return {label:type==="vendor_shipment_cancelled"||data.shipment_status==="cancelled"?"출고 취소":"출고",color:C.ok,icon:"package"};
  if(group==="vendor") return {label:type==="vendor_unit_price_proposed"?"공급처 단가 제안":"공급처 응답",color:C.warn,icon:"building"};
  if(group==="order") return {label:type==="dispatch_failed"?"전달 확인":type==="order_issue"?"확인 필요":"발주",color:C.acc,icon:"clipboard"};
  return {label:type==="payment"?"결제":"서비스 알림",color:C.acc,icon:"bell"};
}

export function getNotificationSender(item){
  const data=normalizeNotificationData(item?.data);
  return compactText(data.sender_name||data.target_name||data.vendor_name||data.factory_name) || getNotificationCategory(item).label;
}

function saveOrderNotificationContext(notification){
  const data=normalizeNotificationData(notification?.data);
  const orderId=String(data?.order_id||notification?.order_id||"").trim();
  if(!orderId||typeof window==="undefined"||!window.sessionStorage) return;

  const payload={
    order_id:orderId,
    id:notification?.id||null,
    type:notification?.type||"",
    title:notification?.title||"",
    body:notification?.body||"",
    data,
    created_at:notification?.created_at||null,
    saved_at:new Date().toISOString()
  };

  try{
    window.sessionStorage.setItem(`dworks:orderNotification:${orderId}`,JSON.stringify(payload));
    window.sessionStorage.setItem("dworks:lastOrderNotification",JSON.stringify(payload));
  }catch(error){
    // sessionStorage 사용이 막힌 환경에서는 기존 order_id 이동만 유지합니다.
  }
}

function getNotificationThreadId(item){
  const data=normalizeNotificationData(item?.data);
  return String(data?.thread_id||data?.threadId||"").trim();
}

function getNotificationActionLabel(item){
  if(isChatNotification(item)) return getNotificationThreadId(item)?"채팅으로 이동":"대화 목록 보기";
  if(getNotificationOrderId(item)) return "발주 내역 보기";
  if(item?.type==="vendor_feedback") return "내역으로 이동";
  return "알림 확인";
}

function getNotificationTitle(item){
  return compactText(item?.title)||"D-Works 알림";
}

function getNotificationBodyLines(item){
  const body=String(item?.body||"새 알림이 도착했습니다.");
  const lines=body
    .split(/\r?\n/)
    .map(line=>compactText(line))
    .filter(Boolean);

  if(lines.length===0) return ["새 알림이 도착했습니다."];

  return lines.slice(0,4);
}

function getNotificationMetaText(item){
  const data=normalizeNotificationData(item?.data);
  const parts=[];

  if(data?.target_name) parts.push(`거래처 ${data.target_name}`);
  if(data?.vendor_name) parts.push(`공급처 ${data.vendor_name}`);
  if(data?.item_name) parts.push(String(data.item_name));
  if(data?.option_name) parts.push(String(data.option_name));

  return parts.map(compactText).filter(Boolean).join(" · ");
}

export function isChatNotification(item){
  const type=String(item?.type||"");
  const data=normalizeNotificationData(item?.data);
  return ["order_chat_message","direct_chat_message"].includes(type) || Boolean(getNotificationThreadId(item)) || ["order_chat","direct_vendor_chat","factory_chat"].includes(data.source);
}

export function getNotificationDestination(item){
  if(isChatNotification(item)) return {page:"chat",threadId:getNotificationThreadId(item),orderId:getNotificationOrderId(item)};
  const orderId=getNotificationOrderId(item);
  if(orderId) return {page:"list",orderId};
  const type=String(item?.type||"");
  if(["vendor_feedback","order_completed","dispatch_failed","order_issue","order","scheduled_order_completed","scheduled_order_failed","factory_production_status","factory_shipment_confirmed","vendor_shipment","vendor_shipment_cancelled","vendor_unit_price_proposed","order_status_changed"].includes(type)) return {page:"list"};
  if(["payment","usage_low","usage_limit_reached"].includes(type)) return {page:"settings"};
  return {page:"notifications"};
}

export { formatNotificationTime, normalizeNotificationData, compactText, readLocalOrderNotifications, writeLocalOrderNotifications, getNotificationOrderId, getNotificationDedupKey, mergeNotificationsWithLocal, markLocalNotificationRead, markAllLocalNotificationsRead, getNotificationRawText, requestNotificationApi, getNotificationCategory, saveOrderNotificationContext, getNotificationThreadId, getNotificationActionLabel, getNotificationTitle, getNotificationBodyLines, getNotificationMetaText };
